import React from 'react';
import clsx from 'clsx';

import { Text } from '@auspost/postmaster-react';

import { TooltipAnchor, TooltipFloat, useTooltipPositionRef, useTooltipState } from 'App/common/Tooltip';
import { FlightStatus } from 'models/shipments';

import { createEddTagInfo, EddTagInfo } from '../../../../utils/edd.utils';
import { useCurrentArticle } from '../../hooks/shipment.hooks';
import {
  EDD_TAG_A11Y_DESC_TEST_ID,
  EDD_TAG_EDD_TEXT_TEST_ID,
  EDD_TAG_FLIGHT_STATUS_TEST_ID,
  EDD_TAG_TEST_ID,
  EDD_TAG_TOOLTIP_FLOAT_TEST_ID,
} from './EddTag.test-ids';

import styles from './EddTag.module.scss';

const getStatusA11yDesc = (edd: EddTagInfo) => {
  const { estimatedDeliveryPrefix, estimatedDeliveryText } = edd;
  let parcelA11yStatus = '';
  let dateDesc = '';
  switch (edd?.flightStatus) {
    case FlightStatus.EXPECTED_TODAY:
      parcelA11yStatus = 'This parcel is expected to be delivered today';
      break;
    case FlightStatus.UPDATED:
      parcelA11yStatus = `The expected arrival date has been updated. ${estimatedDeliveryPrefix}`;
      break;
    case FlightStatus.ON_TIME:
      parcelA11yStatus = `This parcel is on time. ${estimatedDeliveryPrefix} to arrive`;
      break;
    case FlightStatus.LATE:
      parcelA11yStatus = `This parcel is late. ${estimatedDeliveryPrefix} to arrive`;
      break;
  }

  if (estimatedDeliveryText.includes('–')) {
    const dateRange = estimatedDeliveryText.split('-');
    dateDesc = `${dateRange[0]} to ${dateRange[1]}`;
  }
  dateDesc = estimatedDeliveryText;

  return `${parcelA11yStatus} ${dateDesc}`;
};

const EddTag: React.FC = () => {
  const currentArticle = useCurrentArticle();
  const state = useTooltipState({ placement: 'bottom-start' });
  const { positioningRef } = useTooltipPositionRef(state);

  const edd = currentArticle ? createEddTagInfo(currentArticle) : null;
  if (!edd) return null;

  return (
    <>
      <strong className='sr-only' data-testid={EDD_TAG_A11Y_DESC_TEST_ID}>
        {getStatusA11yDesc(edd)}
      </strong>
      <TooltipAnchor
        state={state}
        className={clsx(styles.eddTag, styles[edd.cssClassName])}
        data-testid={EDD_TAG_TEST_ID}
        aria-label='Opens tooltip'
      >
        <div ref={positioningRef} className={styles.flightStatus} data-testid={EDD_TAG_FLIGHT_STATUS_TEST_ID}>
          <Text variant='body-small' weight='bold' aria-hidden={true}>
            {edd.flightStatus}
          </Text>
        </div>
        <div className={styles.dateRange} aria-hidden={true} data-testid={EDD_TAG_EDD_TEXT_TEST_ID}>
          <Text variant='body-small'>
            <Text as='span' variant='body-small'>
              {edd.estimatedDeliveryPrefix}
            </Text>
            <Text as='span' variant='body-small'>
              {` ${edd.estimatedDeliveryText}`}
            </Text>
          </Text>
        </div>
      </TooltipAnchor>
      <TooltipFloat className={styles.eddTooltip} state={state} data-testid={EDD_TAG_TOOLTIP_FLOAT_TEST_ID}>
        {edd.tooltipContent}
      </TooltipFloat>
    </>
  );
};

export default EddTag;
